import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DailyPracticeNotesCard from "./DailyPracticeNotesCard";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAllPracticeNotes,
  getPracticeNotesQR,
} from "../../../services/mentorServices/PracticeNotesServices";
import Footer from "./Footer";

const getPercentage = (part, total) =>
  total ? ((part / total) * 100).toFixed(2) : 0;

const PracticeNoteCard = ({ day, onClickOpen }) => (
  <Card key={day.daily_note_id} sx={{ p: 2, mb: 2, boxShadow: 3 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h6">{day.topic_name || "No Topic"}</Typography>
      <Box display="flex" gap={1}>
        <Button variant="contained" onClick={() => onClickOpen(day, "qr")} title="View QR Code">
          QR
        </Button>
        <Button variant="contained" onClick={() => onClickOpen(day, "view")} title="View Details">
          View
        </Button>
      </Box>
    </Box>
    <Box display="flex" flexDirection="column" gap={1} mt={2}>
      <InfoRow label="Maker Plan" value={day.makerplan_name || "No Maker Plan"} />
      <InfoRow label="Module" value={day.module_name || "No Module"} />
      <InfoRow label="Total Learners" value={day.total_learners || 0} />
      <InfoRow
        label="Started"
        value={`${day.total_learners_started || 0} (${getPercentage(day.total_learners_started, day.total_learners)}%)`}
      />
      <InfoRow
        label="Yet To Start"
        value={`${day.learners_not_started || 0} (${getPercentage(day.learners_not_started, day.total_learners)}%)`}
      />
    </Box>
  </Card>
);

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between">
    <Typography>{label}:</Typography>
    <Typography>{value}</Typography>
  </Box>
);

const DailyPracticeNotesDashboard = () => {
  const location = useLocation();
  const { coeId, labId, labs } = location.state || {};

  const [open, setOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dailyPracticeNotes, setDailyPracticeNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogMode, setDialogMode] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selectedLab, setSelectedLab] = useState(labId);

  const fetchPracticeNotes = useCallback(async () => {
    if (!coeId || !selectedLab) {
      setError("COE and Lab IDs are required.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getAllPracticeNotes(coeId, selectedLab);
      setDailyPracticeNotes(response?.data?.data || []);
      setError(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to fetch practice notes.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [coeId, selectedLab]);

  useEffect(() => {
    fetchPracticeNotes();
  }, [fetchPracticeNotes]);

  const fetchQR = useCallback(async () => {
    if (open && dialogMode === "qr" && selectedDay) {
      setQrLoading(true);
      try {
        const response = await getPracticeNotesQR(selectedDay.daily_note_id);
        if (response && response.data) {
          const blob = new Blob([response.data], { type: "image/png" });
          const url = window.URL.createObjectURL(blob);
          setQrCode(url);
        } else {
          throw new Error("Invalid response data");
        }
      } catch (error) {
        toast.error(error?.response?.data.message || error?.message || "Failed to fetch QR code");
        setQrCode(null);
      } finally {
        setQrLoading(false);
      }
    }
  }, [open, dialogMode, selectedDay]);

  useEffect(() => {
    fetchQR();
  }, [fetchQR]);

  useEffect(() => {
    return () => {
      if (qrCode) {
        window.URL.revokeObjectURL(qrCode);
      }
    };
  }, [qrCode]);

  const handleClickOpen = (day, mode) => {
    setSelectedDay(day);
    setDialogMode(mode);
    setOpen(true);
    if (mode === "qr") {
      setQrCode(null);
      setQrLoading(true);
    }
  };

  const handleClose = () => {
    if (qrCode) {
      window.URL.revokeObjectURL(qrCode);
      setQrCode(null);
    }
    setOpen(false);
    setSelectedDay(null);
    setDialogMode(null);
    setQrLoading(false);
  };

  const labOptions = useMemo(
    () =>
      labs?.map((lab) => (
        <MenuItem key={lab?.id} value={lab?.id}>
          {lab?.lab_name}
        </MenuItem>
      )),
    [labs]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{ p: 2, maxWidth: 400, mx: "auto", bgcolor: "background.paper" }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, maxWidth: 500, mx: "auto", bgcolor: "background.paper", pb: 10,width: "100% !important"  }}>
      <FormControl
        size="small"
        className="lab-name-select"
        sx={{ width: "100% !important", mb: 2 }}
      >
        <InputLabel id="lab-select-label">Lab Name*</InputLabel>
        <Select
          labelId="lab-select-label"
          id="lab-select"
          name="lab"
          value={selectedLab}
          onChange={(event) => setSelectedLab(event.target.value)}
          label="Lab Name"
        >
          {labOptions}
        </Select>
      </FormControl>

      <Typography variant="h6" align="center" fontWeight="bold" mb={2}>
        Daily Practice Notes
      </Typography>

      {dailyPracticeNotes.length > 0 ? (
        dailyPracticeNotes.map((day) => (
          <PracticeNoteCard key={day.daily_note_id} day={day} onClickOpen={handleClickOpen} />
        ))
      ) : (
        <Typography>No daily practice notes found for this COE and lab.</Typography>
      )}

      <Dialog open={open} onClose={handleClose}  maxWidth="500"   >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {selectedDay
            ? `${selectedDay.note_name || "Day"} - ${selectedDay.topic_name || "No Topic"
            } ${dialogMode === "qr" ? "QR Code" : "Details"}`
            : "Daily Practice Notes Details"}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 1, width: "93% !important" }}>
          {dialogMode === "view" && selectedDay && (
            <DailyPracticeNotesCard day={selectedDay}  />
          )}
          {dialogMode === "qr" &&
            (qrLoading ? (
              <CircularProgress />
            ) : qrCode ? (
              <img
                src={qrCode}
                alt="QR Code"
                style={{
                  width: "100%",
                  maxWidth: 400,
                  margin: "auto",
                  display: "block",
                }}
              />
            ) : (
              <Typography>No QR code available</Typography>
            ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Footer coeId={coeId} labId={labId} labs={labs} />
    </Box>
  );
};

export default DailyPracticeNotesDashboard;
